import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'fr', label: 'Français', short: 'FR' },
];

interface LanguageSwitcherProps {
  light?: boolean;
}

export default function LanguageSwitcher({ light = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = languages.find((l) => i18n.language?.startsWith(l.code)) || languages[0];

  const baseText = light ? 'text-background-50' : 'text-foreground-700';
  const hoverText = light ? 'hover:text-background-50' : 'hover:text-foreground-950';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${baseText} ${hoverText}`}
        aria-label="Change language"
      >
        <i className="ri-global-line"></i>
        <span>{current.short}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-36 rounded-lg bg-background-50 border border-background-200/80 shadow-sm py-1.5 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                current.code === lang.code
                  ? 'text-primary-700 font-semibold bg-primary-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {lang.label}
              {current.code === lang.code && <i className="ri-check-line text-primary-600"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}